// Enhanced AppOverview.tsx
import React, { useState } from "react";
import {
  Container,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Box,
  Paper,
  TableContainer,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Divider,
  IconButton,
  Tooltip,
  Grid
} from "@mui/material";
import { Application } from "@microsoft/microsoft-graph-types";

// Import icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TestIcon from "@mui/icons-material/PlayArrow";
import SpaIcon from "@mui/icons-material/Devices";
import WebIcon from "@mui/icons-material/Web";
import AppIcon from "@mui/icons-material/Apps";
import FilterListIcon from "@mui/icons-material/FilterList";
import SecurityIcon from "@mui/icons-material/Security";

interface AppOverviewProps {
  apps: Application[];
  onBack: () => void;
  onSelectAppForTesting?: (app: Application) => void;
}

const AppOverview: React.FC<AppOverviewProps> = ({
  apps,
  onBack,
  onSelectAppForTesting,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredApps = apps.filter((app) =>
    app.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper to get app type icon
  const getAppTypeIcon = (app: Application) => {
    const isSpa = Boolean(app.spa?.redirectUris?.length);
    const isWeb = Boolean(app.web?.redirectUris?.length);
    
    if (isSpa) return <SpaIcon fontSize="small" sx={{ mr: 0.5 }} />;
    if (isWeb) return <WebIcon fontSize="small" sx={{ mr: 0.5 }} />;
    return <AppIcon fontSize="small" sx={{ mr: 0.5 }} />;
  };
  
  // Helper to get app type chip
  const getAppTypeChip = (app: Application) => {
    const isSpa = Boolean(app.spa?.redirectUris?.length);
    const isWeb = Boolean(app.web?.redirectUris?.length);
    
    if (isSpa) {
      return (
        <Chip 
          size="small"
          icon={<SpaIcon />}
          label="SPA" 
          color="primary" 
          variant="outlined"
          sx={{ fontWeight: 500 }} 
        />
      );
    } else if (isWeb) {
      return (
        <Chip 
          size="small" 
          icon={<WebIcon />}
          label="Web" 
          color="secondary" 
          variant="outlined"
          sx={{ fontWeight: 500 }} 
        />
      );
    }
    
    return (
      <Chip 
        size="small" 
        icon={<AppIcon />}
        label="Other" 
        color="default" 
        variant="outlined"
        sx={{ fontWeight: 500 }} 
      />
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Section */}
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              Auth Flow Tester
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Select an application to test its authentication flows
            </Typography>
          </Box>
        </Box>
        
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Search Box */}
            <Grid item xs={12}>
              <TextField
                label="Search Applications"
                variant="outlined"
                fullWidth
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear search"
                        onClick={() => setSearchQuery('')}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  }
                }}
              />
            </Grid>
            
            {/* Results Count */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredApps.length === 0 
                    ? "No applications found" 
                    : `Showing ${filteredApps.length} application${filteredApps.length === 1 ? '' : 's'}`
                  }
                </Typography>
                
                {/* This could be used for future filtering options */}
                <Tooltip title="Filter applications">
                  <IconButton size="small" color="primary">
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
            
          {/* Application Table */}
          {filteredApps.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 4, 
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                mt: 2
              }}
            >
              <AppIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No applications match your search
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search terms or clearing the filter
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setSearchQuery('')}
                sx={{ mt: 2 }}
                startIcon={<ClearIcon />}
              >
                Clear Search
              </Button>
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              elevation={0}
              variant="outlined"
              sx={{ 
                mt: 2, 
                borderRadius: 2,
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Display Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>App Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                
                <TableBody>
                  {filteredApps.map((app) => (
                    <TableRow 
                      key={app.id}
                      sx={{ 
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => onSelectAppForTesting?.(app)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getAppTypeIcon(app)}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {app.displayName}
                          </Typography>
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {app.appId}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>{getAppTypeChip(app)}</TableCell>
                      
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<TestIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectAppForTesting?.(app);
                          }}
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                            '&:hover': {
                              boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                            }
                          }}
                        >
                          Test
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      
      {/* Information Card */}
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f9f9f9' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SecurityIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">About Authentication Testing</Typography>
          </Box>
          
          <Typography variant="body2" paragraph>
            The Auth Flow Tester allows you to test the authentication experience for your applications. 
            You can authenticate as a user, view the resulting tokens, and validate that your application's 
            configuration is working as expected.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Features include:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <Typography variant="body2" component="span" sx={{ mr: 1 }}>•</Typography>
              <Typography variant="body2">Testing authentication flows with actual user accounts</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <Typography variant="body2" component="span" sx={{ mr: 1 }}>•</Typography>
              <Typography variant="body2">Viewing detailed token information and claims</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <Typography variant="body2" component="span" sx={{ mr: 1 }}>•</Typography>
              <Typography variant="body2">Validating application permissions and configurations</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AppOverview;